import React, { useState } from 'react';
import { Table, Button, Input, Space, Tag, message, Dropdown } from 'antd';
import { PlusOutlined, SearchOutlined, MoreOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card } from '../components/design-system/Card';
import ForwarderManagementModal from '../components/common/ForwarderManagementModal';
import api from '../services/api';

type CommunicationType = 'CONTRACTS' | 'BOOKING_REQUESTS' | 'SHIPPING_INSTRUCTIONS' | 'SHIPPING_DOCS' | 'SHIPPING_REPORTS' | 'INVOICES' | 'FINANCIAL_REPORTS' | 'GENERAL';
type RecipientType = 'TO' | 'CC';

interface CommunicationPreference {
  communicationType: CommunicationType;
  recipientType: RecipientType;
  enabled: boolean;
}

interface Contact {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  title?: string;
  department?: string;
  isPrimary: boolean;
  communicationPreferences?: CommunicationPreference[];
}

interface Forwarder {
  id: string;
  company: string;
  active: boolean;
  shipmentCount?: number;
  contacts: Contact[];
  createdAt: string;
  updatedAt: string;
}

interface ForwardersResponse {
  forwarders: Forwarder[];
  total: number;
  limit: number;
  offset: number;
}

const ForwardersList: React.FC = () => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedForwarder, setSelectedForwarder] = useState<Forwarder | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [pageSize, setPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);

  // Fetch forwarders
  const { data: forwardersData, isLoading, error } = useQuery<ForwardersResponse>({
    queryKey: ['forwarders', searchTerm],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);

      // api interceptor returns response.data directly
      return await api.get(`/forwarders?${params.toString()}`);
    },
    refetchInterval: 60000, // Refresh every minute
  });

  const forwarders = forwardersData?.forwarders || [];

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (forwarderId: string) => {
      await api.delete(`/forwarders/${forwarderId}`);
    },
    onSuccess: () => {
      message.success('Forwarder deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['forwarders'] });
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.error || 'Failed to delete forwarder';
      message.error(errorMessage);
      console.error('Delete error:', error);
    },
  });

  const handleCreateForwarder = () => {
    setSelectedForwarder(null);
    setModalMode('create');
    setModalVisible(true);
  };

  const handleEditForwarder = (forwarder: Forwarder) => {
    setSelectedForwarder(forwarder);
    setModalMode('edit');
    setModalVisible(true);
  };

  const handleDeleteForwarder = (forwarderId: string) => {
    deleteMutation.mutate(forwarderId);
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setSelectedForwarder(null);
    queryClient.invalidateQueries({ queryKey: ['forwarders'] });
  };

  const getStatusColor = (active: boolean) => {
    return active ? 'success' : 'default';
  };

  const getCommunicationTypeLabel = (type: CommunicationType): string => {
    const labels: Record<CommunicationType, string> = {
      'CONTRACTS': 'Contracts',
      'BOOKING_REQUESTS': 'Bookings',
      'SHIPPING_INSTRUCTIONS': 'SI',
      'SHIPPING_DOCS': 'Docs',
      'SHIPPING_REPORTS': 'Reports',
      'INVOICES': 'Invoices',
      'FINANCIAL_REPORTS': 'Financial',
      'GENERAL': 'General',
    };
    return labels[type] || type;
  };

  const columns = [
    {
      title: 'Company Name',
      dataIndex: 'company',
      key: 'company',
      sorter: (a: Forwarder, b: Forwarder) => a.company.localeCompare(b.company),
      render: (text: string) => (
        <div className="text-card-subtitle">{text}</div>
      ),
    },
    {
      title: 'Contacts',
      key: 'contacts',
      render: (record: Forwarder) => {
        if (!record.contacts || record.contacts.length === 0) {
          return <span style={{ color: 'var(--text-tertiary)', fontStyle: 'italic' }}>No contacts</span>;
        }

        // Show primary contact first, then others
        const primaryContact = record.contacts.find(c => c.isPrimary);
        const otherContacts = record.contacts.filter(c => !c.isPrimary);
        const displayContacts = primaryContact ? [primaryContact, ...otherContacts] : record.contacts;

        return (
          <div>
            {displayContacts.slice(0, 2).map((contact, index) => (
              <div key={contact.id} style={{ marginBottom: index < displayContacts.length - 1 ? '8px' : 0 }}>
                <div className="text-card-body">
                  {contact.firstName} {contact.lastName}
                  {contact.isPrimary && (
                    <Tag color="blue" style={{ marginLeft: '8px', fontSize: '10px' }}>PRIMARY</Tag>
                  )}
                </div>
                {contact.title && (
                  <div className="text-card-body" style={{ color: 'var(--text-secondary)', fontSize: '11px' }}>
                    {contact.title}
                  </div>
                )}
                <div className="text-card-body" style={{ color: 'var(--text-secondary)', fontSize: '11px' }}>
                  {contact.email}
                </div>
                {contact.phone && (
                  <div className="text-card-body" style={{ color: 'var(--text-secondary)', fontSize: '11px' }}>
                    {contact.phone}
                  </div>
                )}
                {contact.communicationPreferences && contact.communicationPreferences.length > 0 && (
                  <div style={{ marginTop: '4px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                    {contact.communicationPreferences.map((pref, idx) => (
                      <Tag
                        key={idx}
                        color={pref.recipientType === 'TO' ? 'green' : 'blue'}
                        style={{ fontSize: '9px', margin: 0, padding: '0 4px' }}
                      >
                        {getCommunicationTypeLabel(pref.communicationType)} ({pref.recipientType})
                      </Tag>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {record.contacts.length > 2 && (
              <div className="text-card-body" style={{ color: 'var(--text-tertiary)', fontSize: '11px', marginTop: '4px' }}>
                +{record.contacts.length - 2} more
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Shipments',
      dataIndex: 'shipmentCount',
      key: 'shipmentCount',
      sorter: (a: Forwarder, b: Forwarder) => (a.shipmentCount || 0) - (b.shipmentCount || 0),
      render: (count: number) => count || 0,
    },
    {
      title: 'Status',
      dataIndex: 'active',
      key: 'active',
      filters: [
        { text: 'Active', value: true },
        { text: 'Inactive', value: false },
      ],
      onFilter: (value: any, record: Forwarder) => record.active === value,
      render: (active: boolean) => (
        <Tag color={getStatusColor(active)}>
          {active ? 'ACTIVE' : 'INACTIVE'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (record: Forwarder) => (
        <Dropdown
          menu={{
            items: [
              {
                key: 'edit',
                icon: <EditOutlined />,
                label: 'Edit',
                onClick: () => handleEditForwarder(record),
              },
              {
                key: 'delete',
                icon: <DeleteOutlined />,
                label: 'Delete',
                danger: true,
                onClick: () => handleDeleteForwarder(record.id),
              },
            ],
          }}
          trigger={['click']}
        >
          <Button
            type="text"
            icon={<MoreOutlined />}
            size="small"
            style={{ color: 'var(--text-secondary)' }}
          />
        </Dropdown>
      ),
    },
  ];

  if (error) {
    return (
      <div style={{ padding: '48px', textAlign: 'center' }}>
        <h2 className="text-h2" style={{ color: 'var(--color-negative)' }}>Unable to Load Forwarders</h2>
        <p className="text-card-body" style={{ color: 'var(--text-secondary)', marginTop: '16px' }}>
          Please check your connection and try again
        </p>
        <Button
          type="primary"
          onClick={() => window.location.reload()}
          style={{ marginTop: '24px' }}
        >
          Refresh Page
        </Button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px'
      }}>
        <div>
          <h1 className="text-h1">Forwarders</h1>
          <p className="text-card-body" style={{ color: 'var(--text-secondary)', marginTop: '4px' }}>
            Manage freight forwarders and their contacts
          </p>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreateForwarder}
          size="large"
        >
          Add Forwarder
        </Button>
      </div>

      {/* Search and Filters */}
      <Card style={{ marginBottom: '24px' }}>
        <Space size="middle" style={{ width: '100%', flexWrap: 'wrap' }}>
          <Input
            placeholder="Search forwarders..."
            prefix={<SearchOutlined />}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: 300 }}
            allowClear
          />
          <div className="text-card-body" style={{ color: 'var(--text-secondary)' }}>
            {forwarders?.length || 0} forwarders found
          </div>
        </Space>
      </Card>

      {/* Forwarders Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={forwarders}
          loading={isLoading}
          rowKey="id"
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: forwarders?.length || 0,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} forwarders`,
            pageSizeOptions: [10, 20, 50, 100],
            locale: {
              items_per_page: '/ page',
            },
            onChange: (page, newPageSize) => {
              setCurrentPage(page);
              if (newPageSize !== pageSize) {
                setPageSize(newPageSize);
                setCurrentPage(1);
              }
            },
            onShowSizeChange: (current, size) => {
              setPageSize(size);
              setCurrentPage(1);
            },
          }}
          scroll={{ x: 800 }}
        />
      </Card>

      {/* Forwarder Modal */}
      <ForwarderManagementModal
        visible={modalVisible}
        mode={modalMode}
        forwarder={selectedForwarder}
        onClose={handleModalClose}
      />
    </div>
  );
};

export default ForwardersList;
